import { OverlayShimmerText } from '@/components/overlay/overlay-shimmer-text';
import { cn } from '@/lib/cn';
import { ScrollArea } from '@heidi/design-system/scroll-area';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import type { ClinicalTaskResultTask } from '@heidi/tasks/contracts';
import { AlertCircle, SquareCheck } from 'lucide-react';
import { useRouter } from 'next/router';
import { useCallback, useState, type ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { ClinicalTaskRow } from './ClinicalTaskRow';

const m = defineMessages({
  creatingTasks: {
    defaultMessage: 'Creating tasks…',
    id: 'v+9v56mF6A',
    description: 'Running label while clinical tasks are being created',
  },
  tasksCreationFailed: {
    defaultMessage: 'Failed to create tasks',
    id: 'zdWMQtzknT',
    description: 'Error label when clinical task creation fails',
  },
  viewAll: {
    defaultMessage: 'View all',
    id: 'DsP9UzgJm7',
    description: 'Link to open the full tasks surface',
  },
  createdTasks: {
    defaultMessage: 'Created {count, plural, one {# task} other {# tasks}}',
    id: '0+Hk+iG+gY',
    description: 'Header when clinical tasks were created',
  },
  open: {
    defaultMessage: 'Open',
    id: 'VB6Vh4XQ94',
    description: 'Expand inline clinical task preview',
  },
  close: {
    defaultMessage: 'Close',
    id: 'gDQiY3MgwI',
    description: 'Collapse inline clinical task preview',
  },
});

const MAX_VISIBLE_TASKS = 5;

/** Match the agent-todo tool row's entrance so the two sit on the same rhythm. */
const ATOM_ANIMATE_IN =
  'motion-safe:duration-300 motion-safe:ease-out-quart motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1';

interface ClinicalTaskToolBlockProps {
  /**
   * ``proposed`` is the pre-approval twin of ``completed``: same slim row, but
   * the tasks haven't been created yet (the HITL approval banner is rendered
   * beneath it). It starts expanded — the clinician should see what they're
   * approving — and hides the "View all" outlink (the tasks don't exist at
   * ``/tasks`` yet). Pass ``title`` to override the default header copy.
   */
  status: 'creating' | 'completed' | 'proposed' | 'failed';
  tasks: ClinicalTaskResultTask[];
  error?: string;
  title?: ReactNode;
  className?: string;
}

/**
 * Projects-timeline rendering for the `create_clinical_task` / `list_tasks` /
 * `update_task_status` tools. Deliberately mirrors the agent-todo tool row
 * (`AgentTodoResultBlock`): a slim, muted single-line row with a sweeping
 * shimmer while running, and a collapsed-by-default summary that expands to a
 * read-only task list. "View all" jumps to the full `/tasks` surface for
 * editing — the transcript row stays a glanceable receipt.
 *
 * This is intentionally separate from the shared `ClinicalTaskResultBlock`
 * (still used by the legacy chat timeline) so the Projects look can evolve
 * without touching legacy.
 */
export const ClinicalTaskToolBlock = ({
  status,
  tasks,
  error,
  title,
  className,
}: ClinicalTaskToolBlockProps): JSX.Element => {
  const intl = useIntl();
  const router = useRouter();
  // Proposed tasks await approval, so show them up front (the clinician should
  // see what they're approving); the post-creation receipt starts collapsed.
  const [isCollapsed, setIsCollapsed] = useState(status !== 'proposed');

  const handleViewAll = useCallback(() => void router.push('/tasks'), [router]);
  const handleToggleCollapse = useCallback(() => setIsCollapsed((prev) => !prev), []);

  // Running — slim inline row with sweeping shimmer.
  if (status === 'creating') {
    return (
      <div
        className={cn(
          'flex w-full min-w-0 items-center gap-2',
          ATOM_ANIMATE_IN,
          className
        )}
        data-testid="clinical-task-block"
        data-status="creating"
      >
        <SquareCheck className="size-3.5 shrink-0 text-text-tertiary" aria-hidden />
        <OverlayShimmerText className="min-w-0 flex-1 text-sm leading-normal text-text-tertiary">
          {intl.formatMessage(m.creatingTasks)}
        </OverlayShimmerText>
      </div>
    );
  }

  // Failed — slim inline row + error caption.
  if (status === 'failed') {
    return (
      <div
        className={cn('flex w-full min-w-0 flex-col gap-0.5', ATOM_ANIMATE_IN, className)}
        data-testid="clinical-task-block"
        data-status="failed"
      >
        <div className="flex min-w-0 items-center gap-2">
          <AlertCircle
            className="size-3.5 shrink-0 text-validation-destructive"
            aria-hidden
          />
          <TypographyP2 className="text-sm text-validation-destructive">
            {intl.formatMessage(m.tasksCreationFailed)}
          </TypographyP2>
        </div>
        {error ? (
          <TypographyCaption className="ps-5 text-text-tertiary">
            {error}
          </TypographyCaption>
        ) : null}
      </div>
    );
  }

  // Completed / proposed — slim collapsible header + read-only task list.
  const listExpanded = !isCollapsed && tasks.length > 0;
  const headerTitle =
    title ?? intl.formatMessage(m.createdTasks, { count: tasks.length });
  const taskRows = tasks.map((task) => (
    <ClinicalTaskRow key={task.id} title={task.title} category={task.category} />
  ));

  return (
    <div
      className={cn('flex w-full flex-col gap-2', ATOM_ANIMATE_IN, className)}
      data-testid="clinical-task-block"
      data-status={status}
      data-item-count={tasks.length}
    >
      <div className="group/ct-row flex w-full min-w-0 items-center justify-between gap-2">
        <button
          type="button"
          onClick={handleToggleCollapse}
          aria-expanded={!isCollapsed}
          className={cn(
            'flex min-w-0 flex-1 items-center gap-2 text-start',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus'
          )}
        >
          <SquareCheck className="size-3.5 shrink-0 text-text-tertiary" aria-hidden />
          <TypographyP2 className="truncate text-sm leading-normal text-text-tertiary">
            {headerTitle}
          </TypographyP2>
        </button>
        {/* Trailing actions, revealed on hover/focus only and sized like the
            generic inline tool row's "Open" affordance: "View all" outlinks to
            the full /tasks surface; "Open"/"Close" toggles the inline preview
            (rightmost, mirroring the other inline tools). */}
        {tasks.length > 0 ? (
          <div
            className={cn(
              'flex shrink-0 items-center gap-3 text-text-tertiary',
              'pointer-events-none opacity-0 transition-opacity duration-150',
              'group-hover/ct-row:pointer-events-auto group-hover/ct-row:opacity-100',
              'focus-within:pointer-events-auto focus-within:opacity-100'
            )}
          >
            {status !== 'proposed' ? (
              <button
                type="button"
                onClick={handleViewAll}
                className="rounded underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none"
              >
                <TypographyCaption>{intl.formatMessage(m.viewAll)}</TypographyCaption>
              </button>
            ) : null}
            <button
              type="button"
              onClick={handleToggleCollapse}
              className="rounded underline-offset-2 hover:underline focus-visible:underline focus-visible:outline-none"
            >
              <TypographyCaption>
                {isCollapsed ? intl.formatMessage(m.open) : intl.formatMessage(m.close)}
              </TypographyCaption>
            </button>
          </div>
        ) : null}
      </div>

      {listExpanded ? (
        tasks.length > MAX_VISIBLE_TASKS ? (
          <ScrollArea className="max-h-[300px]">
            <div className="flex flex-col gap-2">{taskRows}</div>
          </ScrollArea>
        ) : (
          <div className="flex flex-col gap-2">{taskRows}</div>
        )
      ) : null}
    </div>
  );
};
